import { useLayoutEffect, useRef } from 'react'
import { NetworkStatus } from '@apollo/client'

type SettlingResult<TData> = {
  data?: TData | undefined
  error?: Error | undefined
  loading: boolean
  networkStatus: NetworkStatus
}

type QueryCallbacks<TData> = {
  onCompleted?: (data: TData) => void
  onError?: (error: Error) => void
}

/**
 * The `onCompleted` / `onError` options Apollo 4 removes from `useQuery`,
 * with Apollo 3's trigger: `onCompleted` runs when a request settles into
 * `ready` with data — the first result (cache included), a change of
 * variables, and a refetch or poll only under `notifyOnNetworkStatusChange`.
 * It does not run when another operation's write changes `data`, which is
 * what an effect on `data` would do: the store-seeding and page-resetting
 * callbacks this replaces would then rerun mid-flow. `onError` runs once per
 * distinct error.
 *
 * Prefer deriving from `data`; this is for state that has to be seeded once
 * or a side effect that must not repeat.
 *
 * Twin of `apps/portal/src/hooks/useQueryCallbacks.ts`; change both.
 */
export function useQueryCallbacks<TData>(
  result: SettlingResult<TData>,
  callbacks: QueryCallbacks<TData>,
): void {
  const latest = useRef(callbacks)
  useLayoutEffect(() => {
    latest.current = callbacks
  })

  const previous = useRef<{ networkStatus?: NetworkStatus; error?: Error }>({})
  const { data, error, loading, networkStatus } = result
  // A layout effect, so a callback lands before the commit's passive effects
  // as Apollo 3's did. Run after a child's <Navigate>, a callback that sets
  // search params navigates back to the path the child just redirected from.
  useLayoutEffect(() => {
    const before = previous.current
    previous.current = { networkStatus, error }
    if (loading) return
    if (error) {
      if (error !== before.error) latest.current.onError?.(error)
      return
    }
    if (
      data &&
      networkStatus === NetworkStatus.ready &&
      before.networkStatus !== NetworkStatus.ready
    ) {
      latest.current.onCompleted?.(data)
    }
  }, [data, error, loading, networkStatus])
}
